import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { Trophy, Plus, Crown, Sparkles, X, Settings, ArrowLeft, Calendar, Gift } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    registration: { label: "تسجيل مفتوح 🟢", cls: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" },
    live: { label: "البطولة جارية 🔥", cls: "bg-destructive/20 text-destructive border border-destructive/40 font-black animate-pulse" },
    finished: { label: "منتهية 🏆", cls: "bg-white/5 text-white/50 border border-white/10" },
  };
  const m = map[status] || map.finished;
  return <span className={`text-[10px] uppercase font-bold tracking-widest px-3 py-1 rounded-full ${m.cls}`}>{m.label}</span>;
}

function CreateTournamentModal({ onClose, onCreated }) {
  const scheduled = new Date(Date.now() + 24 * 3600 * 1000);
  const defaultDateTime = new Date(scheduled.getTime() - scheduled.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  const [form, setForm] = useState({ 
    name: "", 
    description: "", 
    rules: "", 
    prize: "", // 🎁 أضفنا حقل الجائزة للبطولة
    max_participants: 16, 
    match_format: "bo3",
    starts_at: defaultDateTime,
    round_formats: {
      default: "bo3",
      quarter_finals: "bo3",
      semi_finals: "bo5",
      finals: "bo7",
    }, 
    losers_bracket: false 
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        starts_at: new Date(form.starts_at).toISOString()
      };
      const { data } = await api.post("/tournaments", payload);
      toast.success("تم إنشاء البطولة بنجاح! 🎉");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200" dir="rtl">
      <form onSubmit={submit} className="bg-[#141417] border border-white/15 rounded-3xl p-6 md:p-8 w-full max-w-lg space-y-4 shadow-2xl relative max-h-[90vh] overflow-y-auto" data-testid="create-tournament-form">
        <button type="button" onClick={onClose} className="absolute top-5 left-5 text-white/40 hover:text-white transition p-1.5 rounded-lg hover:bg-white/5">
          <X size={18} />
        </button>

        <h2 className="font-display font-black text-2xl text-white">إطلاق بطولة جديدة 🏆</h2>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1 block">اسم البطولة *</label>
          <input
            data-testid="t-name-input"
            required minLength={2} maxLength={80}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="مثال: بطولة أبطال رايفلز"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-gold-500/50 text-sm text-white transition"
          />
        </div>

        {/* 🎁 خانة إدخال جائزة البطولة */}
        <div>
          <label className="text-xs font-bold text-gold-400 mb-1 flex items-center gap-1.5">
            <Gift size={14} /> جائزة البطولة (مبلغ أو جوائز)
          </label>
          <input
            type="text"
            value={form.prize}
            onChange={(e) => setForm({ ...form, prize: e.target.value })}
            placeholder="مثال: 500 ريال أو 1000 نقطة RIV"
            className="w-full bg-black/40 border border-gold-500/30 rounded-xl px-4 py-3 outline-none focus:border-gold-500 text-sm text-white transition"
          />
        </div>

        {/* 🕒 خانة تحديد وقت وموعد انطلاق البطولة */}
        <div>
          <label className="text-xs font-bold text-gold-400 mb-1 flex items-center gap-1.5">
            <Calendar size={14} /> موعد ووقت بدء البطولة *
          </label>
          <input
            type="datetime-local"
            required
            value={form.starts_at}
            onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
            className="w-full bg-black/40 border border-gold-500/30 rounded-xl px-4 py-3 outline-none focus:border-gold-500 text-sm text-white font-mono transition"
          />
          <span className="text-[10px] text-white/40 mt-1 block">حدد متى سيتم لعب المنافسات وبدء الترتيب.</span>
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1 block">وصف البطولة</label>
          <textarea
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="اكتب وصفاً مختصراً عن جوائز وحماس البطولة..."
            rows={2}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-gold-500/50 resize-none text-sm text-white transition"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1 block">قوانين البطولة</label>
          <textarea
            data-testid="t-rules-input"
            value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })}
            placeholder="القوانين الأساسية والشروط..."
            rows={2}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-gold-500/50 resize-none text-sm text-white transition"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1.5 block">الحد الأقصى للكلانات المشاركة</label>
          <select
            data-testid="t-size-select"
            value={form.max_participants}
            onChange={(e) => setForm({ ...form, max_participants: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none text-white font-mono text-sm"
          >
            {[2, 4, 8, 12, 16].map((n) => <option key={n} value={n} className="bg-[#141417] text-white">{n} كلان</option>)}
          </select>
        </div>

        {/* 🏆 قسم إعدادات نظام الأشواط لكل دور (Best Of) */}
        <div className="bg-black/40 border border-white/10 rounded-2xl p-4 space-y-3 shadow-inner">
          <div className="text-xs font-bold text-gold-400 flex items-center gap-1.5">
            <Settings size={15} /> نظام مواجهات الأدوار (Best Of)
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <label className="block text-white/60 mb-1 font-bold">الأدوار التمهيدية</label>
              <select
                value={form.round_formats.default}
                onChange={(e) => setForm({
                  ...form,
                  round_formats: { ...form.round_formats, default: e.target.value }
                })}
                className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 outline-none text-white font-mono"
              >
                <option value="bo1" className="bg-[#141417]">BO1 (شوط واحد)</option>
                <option value="bo3" className="bg-[#141417]">BO3 (الأفضل من 3)</option>
                <option value="bo5" className="bg-[#141417]">BO5 (الأفضل من 5)</option>
              </select>
            </div>

            <div>
              <label className="block text-white/60 mb-1 font-bold">ربع النهائي</label>
              <select
                value={form.round_formats.quarter_finals}
                onChange={(e) => setForm({
                  ...form,
                  round_formats: { ...form.round_formats, quarter_finals: e.target.value }
                })}
                className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 outline-none text-white font-mono"
              >
                <option value="bo3" className="bg-[#141417]">BO3 (الأفضل من 3)</option>
                <option value="bo5" className="bg-[#141417]">BO5 (الأفضل من 5)</option>
                <option value="bo7" className="bg-[#141417]">BO7 (الأفضل من 7)</option>
              </select>
            </div>

            <div>
              <label className="block text-white/60 mb-1 font-bold">نصف النهائي</label>
              <select
                value={form.round_formats.semi_finals}
                onChange={(e) => setForm({
                  ...form,
                  round_formats: { ...form.round_formats, semi_finals: e.target.value }
                })}
                className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 outline-none text-white font-mono"
              >
                <option value="bo3" className="bg-[#141417]">BO3 (الأفضل من 3)</option>
                <option value="bo5" className="bg-[#141417]">BO5 (الأفضل من 5)</option>
                <option value="bo7" className="bg-[#141417]">BO7 (الأفضل من 7)</option>
              </select>
            </div>

            <div>
              <label className="block text-gold-400 font-bold mb-1">المباراة النهائية</label>
              <select
                value={form.round_formats.finals}
                onChange={(e) => setForm({
                  ...form,
                  round_formats: { ...form.round_formats, finals: e.target.value }
                })}
                className="w-full bg-black/50 border border-gold-500/40 rounded-xl p-2.5 outline-none text-gold-400 font-bold font-mono"
              >
                <option value="bo3" className="bg-[#141417]">BO3 (الأفضل من 3)</option>
                <option value="bo5" className="bg-[#141417]">BO5 (الأفضل من 5)</option>
                <option value="bo7" className="bg-[#141417]">BO7 (الأفضل من 7)</option>
              </select>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer text-xs py-1">
          <input
            data-testid="t-losers-bracket"
            type="checkbox"
            disabled
            checked={form.losers_bracket}
            onChange={(e) => setForm({ ...form, losers_bracket: e.target.checked })}
            className="accent-gold-500 h-4 w-4 rounded"
          />
          <span className="text-white/80 font-bold">الفرصة الثانية غير متاحة حاليًا — البطولة خروج مغلوب</span>
        </label>

        <p className="text-[11px] text-gold-400/90 bg-gold-500/10 p-3 rounded-xl border border-gold-500/20 leading-relaxed">
          ✨ ميزة حصرية: أول 24 ساعة سيكون التسجيل متاحاً لكلانات Plus فقط، ثم يفتح التسجيل لجميع الكلانات تلقائياً.
        </p>

        <div className="flex gap-2.5 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-xs text-white/70 font-bold hover:bg-white/5 transition">
            إلغاء
          </button>
          <button data-testid="submit-tournament" type="submit" className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 text-xs shadow-md transition">
            إطلاق البطولة
          </button>
        </div>
      </form>
    </div>
  );
}

export default function TournamentsPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const requestRef = useRef(null);

  const load = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const { data } = await api.get("/tournaments", { signal: controller.signal, timeout: 12000 });
      if (!controller.signal.aborted) setList(data || []);
    } catch {
      // retry on fail
    }
  }, []);

  useEffect(() => {
    let stopped = false, timer;
    const poll = async () => {
      if (!document.hidden) await load();
      if (!stopped) timer = setTimeout(poll, 15000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); requestRef.current?.abort(); };
  }, [load]);

  const isStaff = user && (user.role === "admin" || user.role === "owner");

  return (
    <div className="space-y-8" dir="rtl">
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-500 text-xs font-bold mb-2">
            <Trophy size={14} /> منافسات الكلان
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl text-white">
            البطولات والتصفيات
          </h1>
          <p className="text-white/50 mt-1.5 text-sm">
            نظام خروج المغلوب الرسمي • Call of Duty
          </p>
        </div>
        {isStaff && (
          <button data-testid="create-tournament-btn" onClick={() => setShowCreate(true)} className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 flex items-center gap-2 text-xs shadow-[0_0_15px_rgba(255,204,0,0.3)] transition">
            <Plus size={16} /> بطولة جديدة
          </button>
        )}
      </div>

      {/* ── Tournaments Grid ── */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => {
          const now = new Date();
          const plusUntil = new Date(t.plus_window_until);
          const plusOnly = t.status === "registration" && now < plusUntil;
          const formatLabel = [...new Set(Object.values(t.round_formats || { default: t.match_format || "bo3" }))].join(" / ").toUpperCase();

          return (
            <Link
              key={t.id}
              to={`/tournaments/${t.id}`}
              data-testid={`tournament-${t.id}`}
              className="bg-[#111113]/90 border border-white/10 hover:border-gold-500/40 rounded-3xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col justify-between group shadow-lg"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <StatusBadge status={t.status} />
                  <div className="text-xs text-white/50 font-mono font-bold flex items-center gap-2">
                    <span>{t.participants?.length || 0}/{t.max_participants} كلان</span>
                    <span className="text-gold-400 bg-black/40 px-2 py-0.5 rounded-lg border border-white/10 font-bold">{formatLabel}</span>
                  </div>
                </div>

                <h3 className="font-display font-black text-xl text-white group-hover:text-gold-400 transition truncate">{t.name}</h3>
                
                {/* 🎁 عرض جائزة البطولة على البطاقة */}
                {t.prize && (
                  <div className="flex items-center gap-1.5 text-xs text-gold-400 font-bold mt-2 bg-gold-500/10 px-3 py-1.5 rounded-xl border border-gold-500/20 w-fit">
                    <Gift size={13} />
                    <span>الجائزة: {t.prize}</span>
                  </div>
                )}

                {/* 🕒 عرض وقت انطلاق البطولة على البطاقة */}
                {t.starts_at && (
                  <div className="flex items-center gap-1.5 text-xs text-white/70 font-mono mt-1.5 bg-black/30 px-3 py-1.5 rounded-xl border border-white/5 w-fit">
                    <Calendar size={13} />
                    <span>الانطلاق: {new Date(t.starts_at).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" })}</span>
                  </div>
                )}

                {t.description && <p className="text-xs text-white/60 mt-2 line-clamp-2 leading-relaxed">{t.description}</p>}
              </div>

              <div className="mt-5 pt-3.5 border-t border-white/5 flex items-center justify-between text-xs">
                {plusOnly ? (
                  <div className="inline-flex items-center gap-1 font-bold text-gold-400 bg-gold-500/10 px-2.5 py-1 rounded-xl border border-gold-500/20">
                    <Sparkles size={12} /> Plus فقط حالياً
                  </div>
                ) : t.status === "finished" && t.champion_clan_id ? (
                  <div className="inline-flex items-center gap-1.5 font-black text-gold-400">
                    <Crown size={15} /> البطل: {t.clans?.[t.champion_clan_id]?.name || "—"}
                  </div>
                ) : (
                  <span className="text-white/40 font-medium">{t.status === "registration" ? "تسجيل متاح للجميع" : "التسجيل مغلق"}</span>
                )}

                <span className="text-gold-500 font-bold inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                  التفاصيل <ArrowLeft size={13} />
                </span>
              </div>
            </Link>
          );
        })}
        {list.length === 0 && (
          <div className="col-span-full bg-[#111113]/40 border border-white/5 rounded-3xl p-16 text-center text-white/40 text-xs">
            لا توجد بطولات قائمة حالياً. ترقّب إطلاق البطولات القادمة!
          </div>
        )}
      </div>

      {showCreate && (
        <CreateTournamentModal
          onClose={() => setShowCreate(false)}
          onCreated={(t) => { setShowCreate(false); window.location.href = `/tournaments/${t.id}`; }}
        />
      )}
    </div>
  );
}
